import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import type { ToteSportsDetails, ToteSportsSelection } from '@mobi/betslip/types'
import type { QuickbetSelection } from '@core/Areas/Quickbet/types'
import { Selections } from '@core/Areas/Tipping/Store'
import { selectTippingSelections } from '@core/Areas/Tipping/Store/selectors'
import { ToteSportsPool, getPool, queryDefaults } from '@core/Areas/Tipping/data/toteSports'
import { calculateSelectionString } from '@core/Areas/Tipping/helpers/calculator'
import { BettingDrawer } from '@core/Components/BettingDrawer'
import { queryKeys } from '@core/Data/ReactQuery/constants'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'

const EmptyBettingDrawerStyled = styled.div({ height: '8rem' })

export const TippingBettingDrawer = ({ poolKey }: { poolKey: string }): JSX.Element | null => {
  const selections = useSelector(selectTippingSelections)

  const {
    isLoading,
    isError,
    data: pool,
  } = useQuery<ToteSportsPool>({
    ...queryDefaults,
    queryKey: [queryKeys.toteSportsPool, poolKey],
    queryFn: () => getPool(poolKey),
  })

  if (isLoading || isError || !pool || !selections) return null

  const onQuickbetClick = (): void => {
    handleBetSelection({
      betFlow: { location: 'Quickbet' },
      selections: [createSelection(pool, selections)],
    })
  }

  const isValidBet =
    Object.keys(selections).length === pool.games.length &&
    Object.values(selections).every(
      selection => selection.isHome || selection.isDraw || selection.isAway
    )

  return (
    <>
      <BettingDrawer open={isValidBet} handleQuickbetClick={onQuickbetClick} />
      {isValidBet && <EmptyBettingDrawerStyled />}
    </>
  )
}

const createSelection = (pool: ToteSportsPool, selections: Selections): QuickbetSelection => {
  const selection: ToteSportsSelection = {
    poolSequence: pool.seq,
    betSelections: calculateSelectionString(selections),
  }

  const selectionDetails: ToteSportsDetails = {
    poolName: `${pool.name} ${pool.nameSuffix} ${pool.numberOfGames}`,
    games: pool.games.map(game => ({
      homeTeamName: game.homeTeamName,
      awayTeamName: game.awayTeamName,
    })),
  }

  return {
    bettingType: 'tote-sports-tipping',
    selection,
    selectionDetails,
    isEachWayAvailable: false,
    shouldAllowPlaceInvestment: false,
  }
}
